import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CheckIcon, ChevronDownIcon } from "./icons";

// Stat card from the redesigned dashboard: a tinted icon tile, an optional
// status badge, the headline metric with a caption, and a full-bleed action
// bar along the bottom. Dark mode swaps the white surface for the navy panel
// and adds a diagonal tone wash in the lower-right corner, matching the dark
// mockup.

export type CardTone = "blue" | "violet" | "green" | "amber" | "slate";
export type BadgeTone = "success" | "partial" | "ontrack" | "attention";

// Diagonal tone wash shown behind a dark-mode card (the mockups paint a soft
// tinted wedge from the lower-right corner). Rendered inline so each tone can
// carry its own colour without emitting a utility per shade.
const TONE_WASH: Record<CardTone, string> = {
  blue: "linear-gradient(135deg, rgba(43,108,220,0) 44%, rgba(43,108,220,0.22) 72%, rgba(43,108,220,0.5) 100%)",
  violet: "linear-gradient(135deg, rgba(124,77,214,0) 44%, rgba(124,77,214,0.2) 72%, rgba(124,77,214,0.46) 100%)",
  green: "linear-gradient(135deg, rgba(30,158,90,0) 44%, rgba(30,158,90,0.18) 72%, rgba(30,158,90,0.42) 100%)",
  amber: "linear-gradient(135deg, rgba(201,130,25,0) 44%, rgba(201,130,25,0.18) 72%, rgba(201,130,25,0.42) 100%)",
  slate: "linear-gradient(135deg, rgba(75,100,134,0) 44%, rgba(75,100,134,0.2) 72%, rgba(75,100,134,0.44) 100%)",
};

const ICON_TONES: Record<CardTone, string> = {
  blue: "bg-[#E8F0FE] text-[#2767D5] dark:bg-[#1B2E52] dark:text-[#79A9FF]",
  violet: "bg-[#EEE9FD] text-[#7C4DD6] dark:bg-[#2A2352] dark:text-[#B79CFF]",
  green: "bg-[#E3F6EC] text-[#1E9E5A] dark:bg-[#102E23] dark:text-[#5FD39A]",
  amber: "bg-[#FDF0D9] text-[#C07A16] dark:bg-[#3A2A12] dark:text-[#F0B968]",
  slate: "bg-[#EAEFF6] text-[#4B6486] dark:bg-[#1E2C46] dark:text-[#9FB3D1]",
};

const BADGE_TONES: Record<BadgeTone, string> = {
  success: "bg-[#E3F6EC] text-[#1E9E5A] dark:bg-[#123326] dark:text-[#5FD39A]",
  partial: "bg-[#FDF0D9] text-[#C07A16] dark:bg-[#3A2A12] dark:text-[#F0B968]",
  ontrack: "bg-[#E3F6EC] text-[#1E9E5A] dark:bg-[#123326] dark:text-[#5FD39A]",
  attention: "bg-[#FDEBD9] text-[#C0770F] dark:bg-[#3A2A12] dark:text-[#F0B968]",
};

const FOOTER_TONES: Record<CardTone, string> = {
  blue: "bg-[#EDF3FE] text-[#2767D5] hover:bg-[#E2ECFD] dark:bg-[#15243E] dark:text-[#79A9FF] dark:hover:bg-[#1A2C4B]",
  violet: "bg-[#F1ECFD] text-[#6A3FCC] hover:bg-[#E9E1FC] dark:bg-[#1D1838] dark:text-[#B79CFF] dark:hover:bg-[#241D45]",
  green: "bg-[#E9F8EF] text-[#1E9E5A] hover:bg-[#DDF4E7] dark:bg-[#10281D] dark:text-[#5FD39A] dark:hover:bg-[#143222]",
  amber: "bg-[#FDF3E2] text-[#C0770F] hover:bg-[#FBEDD5] dark:bg-[#2C2113] dark:text-[#F0B968] dark:hover:bg-[#372A17]",
  slate: "bg-[#EEF2F7] text-[#4B6486] hover:bg-[#E4EAF2] dark:bg-[#1A2436] dark:text-[#9FB3D1] dark:hover:bg-[#223049]",
};

export function OverviewCard({
  icon,
  tone = "blue",
  badge,
  badgeTone = "success",
  title,
  value,
  caption,
  footer,
  footerTone,
  href,
  onFooterClick,
  footerDisabled,
}: {
  icon: ReactNode;
  tone?: CardTone;
  badge?: string;
  badgeTone?: BadgeTone;
  title: string;
  value: ReactNode;
  caption?: ReactNode;
  footer: ReactNode;
  footerTone?: CardTone;
  href?: string;
  onFooterClick?: () => void;
  footerDisabled?: boolean;
}) {
  const footerClass = `relative -mx-5 -mb-5 mt-4 flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors ${
    FOOTER_TONES[footerTone ?? tone]
  }`;

  const footerInner = (
    <>
      {footer}
      <ChevronDownIcon className="ml-auto h-4 w-4 -rotate-90" />
    </>
  );

  return (
    <section className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#E4E9F2] bg-white p-5 shadow-sm dark:border-[#1E2E4D] dark:bg-panel">
      {/* Diagonal tone wash, visible in dark mode only. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 hidden dark:block"
        style={{ backgroundImage: TONE_WASH[tone] }}
      />

      <div className="relative flex items-start justify-between gap-3">
        <span className={`grid h-11 w-11 place-items-center rounded-xl ${ICON_TONES[tone]}`}>{icon}</span>
        {badge && (
          <span className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${BADGE_TONES[badgeTone]}`}>
            {badgeTone === "success" || badgeTone === "ontrack" ? (
              <span className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full bg-current">
                <CheckIcon className="h-2.5 w-2.5 text-white" strokeWidth={3.2} />
              </span>
            ) : (
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
            )}
            {badge}
          </span>
        )}
      </div>

      <div className="relative mt-4 flex-1">
        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">{title}</div>
        <div className="mt-1 text-3xl font-bold leading-none tabular-nums text-slate-900 dark:text-white">{value}</div>
        {caption && <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{caption}</div>}
      </div>

      {href ? (
        <Link to={href} className={footerClass}>
          {footerInner}
        </Link>
      ) : (
        <button type="button" onClick={onFooterClick} disabled={footerDisabled} className={`${footerClass} disabled:cursor-not-allowed disabled:opacity-60`}>
          {footerInner}
        </button>
      )}
    </section>
  );
}
